import React from 'react';
import { AlertTriangle, CheckCircle2, Gauge } from 'lucide-react';
import type { StrategyStatus } from '../services/api';
import { formatCurrency } from '../utils/currency';

interface StrategyMarginStatusProps {
  status?: StrategyStatus['data'] | null;
  compact?: boolean;
}

const StrategyMarginStatus: React.FC<StrategyMarginStatusProps> = ({ status, compact = false }) => {
  const required = status?.free_margin_required;
  const available = status?.free_margin_available;
  const sufficient = status?.free_margin_available_for_strategy;
  const currency = status?.free_margin_currency || 'USD';

  const badge = sufficient === true
    ? {
        label: 'Available',
        className: 'border-emerald-400/20 bg-emerald-400/10 text-emerald-300',
        icon: <CheckCircle2 className="h-3.5 w-3.5" />,
      }
    : sufficient === false
      ? {
          label: 'Shortfall',
          className: 'border-rose-400/20 bg-rose-400/10 text-rose-300',
          icon: <AlertTriangle className="h-3.5 w-3.5" />,
        }
      : {
          label: 'Unavailable',
          className: 'border-white/10 bg-white/5 text-muted',
          icon: <Gauge className="h-3.5 w-3.5" />,
        };

  return (
    <div className={`rounded-xl border border-white/10 bg-black/20 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.16em] text-muted">Free margin</div>
          <div className="mt-1 text-xs text-white/60">
            {status?.free_margin_basis || 'Waiting for strategy sizing'}
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${badge.className}`}>
          {badge.icon}
          {badge.label}
        </div>
      </div>
      <div className={`mt-3 grid gap-3 ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-3'}`}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted">Required</div>
          <div className="mt-1 font-mono text-sm font-semibold text-white">
            {formatCurrency(required, currency)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted">Available</div>
          <div className="mt-1 font-mono text-sm font-semibold text-white">
            {formatCurrency(available, currency)}
          </div>
        </div>
        {!compact && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted">Shortfall</div>
            <div className={`mt-1 font-mono text-sm font-semibold ${status?.free_margin_shortfall ? 'text-rose-300' : 'text-emerald-300'}`}>
              {formatCurrency(status?.free_margin_shortfall, currency)}
            </div>
          </div>
        )}
      </div>
      <div className="mt-3 text-[10px] leading-relaxed text-white/40">
        Estimated from current strategy sizing; IB available funds are the account availability check.
      </div>
    </div>
  );
};

export default StrategyMarginStatus;
